import dayjs from 'dayjs';
import { filter, isEmpty, map, some } from 'lodash';
import { IComponent, IExpiredStatus, IStatus } from 'src/interfaces';
import {
  getExpiryStatusMessage,
  isEligibleForSelection,
  lang,
} from 'src/utils';

import {
  IBulkReplaceLocationGroup,
  IBulkReplaceScheduleItem,
  IBulkReplaceScheduleRow,
} from './BulkReplaceComponentPage.interface';

/**
 * Builds the key used to track a selected schedule item.
 *
 * The schedule item `id` returned by `/scheduleList` is NOT unique across
 * location groups (the same default / future-default assembly assigned to
 * multiple location groups comes back with a repeated `id`). Keying selection
 * state by `id` alone collapses those duplicates, undercounting the impacted
 * location groups. Scoping the key by `location_group_id` keeps them distinct.
 */
export const getScheduleItemSelectionKey = (
  locationGroupId: number,
  itemId: number,
) => `${locationGroupId}-${itemId}`;

const formatDateRange = (startDate: string, endDate?: string | null) => {
  const formattedStartDate = dayjs(startDate).format('MM-DD-YYYY');
  const formattedEndDate = endDate ? dayjs(endDate).format('MM-DD-YYYY') : '';

  return formattedEndDate
    ? `${formattedStartDate} - ${formattedEndDate}`
    : formattedStartDate;
};

const getTableColumns = (reviewMode?: boolean) => {
  const indentColumn = { width: '6%', label: '', id: 'indent' };
  const baseColumns = [
    {
      id: 'schedule_name',
      label: 'Schedule name',
      width: '30%',
      align: 'left',
    },
    {
      id: 'daypart',
      label: 'Daypart',
      width: 'auto',
      align: 'left',
    },
    {
      id: 'position',
      label: 'Position',
      width: 'auto',
      align: 'left',
    },
    {
      id: 'date',
      label: 'Date',
      width: 'auto',
      align: 'left',
    },
  ];

  if (reviewMode) {
    return [
      indentColumn,
      ...baseColumns,
      {
        id: 'status',
        label: 'Status',
        width: '30%',
        align: 'left',
      },
    ];
  }

  return [
    indentColumn,
    {
      id: 'checkbox',
      label: '',
      width: '6%',
      align: 'center',
    },
    ...baseColumns,
  ];
};

const generateTableRows = (
  locationGroupId: number,
  scheduleItems: IBulkReplaceScheduleItem[],
  selectedScheduleItems?: Set<string>,
  onSelectScheduleItem?: (
    locationGroupId: number,
    scheduleItemId: number,
  ) => void,
  reviewMode?: boolean,
  replaceComponent?: IComponent,
): IBulkReplaceScheduleRow[] => {
  let itemsToDisplay = scheduleItems || [];

  if (reviewMode && selectedScheduleItems) {
    itemsToDisplay = itemsToDisplay.filter((item) =>
      selectedScheduleItems.has(
        getScheduleItemSelectionKey(locationGroupId, item.id),
      ),
    );
  }

  return map(itemsToDisplay, (item: IBulkReplaceScheduleItem) => {
    const baseRowData = {
      id: item.id,
      locationGroupId: locationGroupId,
      indent: '',
      schedule_name: item.scheduleName || '',
      daypart: item.daypart_name || 'Unknown',
      position: `Position ${item.position}`,
      date: formatDateRange(item.start_date, item?.end_date),
      status: getBulkReplaceComponentReviewStatus(item, replaceComponent),
    };

    if (!reviewMode) {
      const isSelected =
        selectedScheduleItems?.has(
          getScheduleItemSelectionKey(locationGroupId, item.id),
        ) || false;

      return {
        ...baseRowData,
        checkbox: {
          isSelected,
          onSelect: (e: React.MouseEvent) => {
            e.stopPropagation();
            onSelectScheduleItem?.(locationGroupId, item.id);
          },
        },
      };
    }

    return baseRowData;
  });
};

export const mapScheduleListToTableRows = (
  scheduleList?: {
    data: IBulkReplaceLocationGroup[];
    totalPages: number;
    totalCount: number;
  },
  selectedLocationGroups?: Set<number>,
  selectedScheduleItems?: Set<string>,
  onSelectScheduleItem?: (
    locationGroupId: number,
    scheduleItemId: number,
  ) => void,
  reviewMode?: boolean,
  replaceComponent?: IComponent,
) => {
  if (isEmpty(scheduleList?.data)) return [];

  let locationGroups = scheduleList?.data || [];

  if (reviewMode) {
    const hasSelectedScheduleItems =
      selectedScheduleItems && selectedScheduleItems.size > 0;
    const hasSelectedLocationGroups =
      selectedLocationGroups && selectedLocationGroups.size > 0;

    if (hasSelectedScheduleItems || hasSelectedLocationGroups) {
      locationGroups = filter(locationGroups, (group) => {
        if (
          hasSelectedLocationGroups &&
          selectedLocationGroups.has(group.location_group_id)
        ) {
          return true;
        }

        if (hasSelectedScheduleItems) {
          const hasSelectedItems = some(group.scheduleItems, (item) =>
            selectedScheduleItems.has(
              getScheduleItemSelectionKey(group.location_group_id, item.id),
            ),
          );

          return hasSelectedItems;
        }

        return false;
      });
    }
  }

  return map(locationGroups, (locationGroup) => {
    const scheduleItems = locationGroup.scheduleItems || [];
    let totalItemsInGroup = scheduleItems.length;
    let selectedItemsInGroup = 0;

    if (selectedScheduleItems) {
      selectedItemsInGroup = filter(scheduleItems, (item) =>
        selectedScheduleItems.has(
          getScheduleItemSelectionKey(locationGroup.location_group_id, item.id),
        ),
      ).length;
    }

    const scheduleRows = generateTableRows(
      locationGroup.location_group_id,
      scheduleItems,
      selectedScheduleItems,
      onSelectScheduleItem,
      reviewMode,
      replaceComponent,
    );

    const isGroupSelected = selectedLocationGroups?.has(
      locationGroup.location_group_id,
    );

    const isIndeterminate = Boolean(
      totalItemsInGroup > 0 &&
        selectedItemsInGroup > 0 &&
        selectedItemsInGroup < totalItemsInGroup &&
        !reviewMode,
    );

    const numberOfSchedules = reviewMode
      ? selectedItemsInGroup
      : scheduleItems.length;

    return {
      id: locationGroup.location_group_id,
      location_group: locationGroup.location_group_name,
      channel_type: locationGroup.layout_type,
      number_of_schedules: numberOfSchedules.toString(),
      isSelected: isGroupSelected,
      isIndeterminate,
      isExpanded: false,
      columns: getTableColumns(reviewMode),
      rows: scheduleRows,
      schedules: [],
    };
  });
};

export const hasDateConflict = (
  scheduleItem: IBulkReplaceScheduleItem,
  replacement?: IComponent,
) => {
  if (!replacement) return false;

  const scheduleItemEndDate = scheduleItem.end_date;
  const replacementEndDate = replacement.end_date;

  if (scheduleItemEndDate === null && replacementEndDate) {
    return true;
  }

  if (scheduleItemEndDate && replacementEndDate) {
    return new Date(scheduleItemEndDate) > new Date(replacementEndDate);
  }

  return false;
};

export const getBulkReplaceComponentReviewStatus = (
  scheduleItem: IBulkReplaceScheduleItem,
  replacement?: IComponent,
): IBulkReplaceScheduleRow['status'] => {
  if (hasDateConflict(scheduleItem, replacement)) {
    return {
      message: lang.bulkReplaceComponentPage.reviewChanges.notReplacedInfo,
      type: 'error',
    };
  }

  if (scheduleItem?.has_overrides) {
    return {
      message: lang.bulkReplaceComponentPage.reviewChanges.overrideInfo,
      type: 'warning',
    };
  }

  return {
    message: lang.bulkReplaceComponentPage.reviewChanges.replacedInfo,
    type: 'success',
  };
};

export const validateComponentForBulkReplace = (
  original?: IComponent,
  replacement?: IComponent,
): boolean => {
  const isReady = (c?: IComponent) => c?.status === IStatus.READY;

  if (!isReady(original) || !isReady(replacement)) {
    return false;
  }

  if (
    !isEligibleForSelection(original) ||
    !isEligibleForSelection(replacement)
  ) {
    return false;
  }

  const sameDimensions =
    original?.width === replacement?.width &&
    original?.height === replacement?.height;

  if (!sameDimensions) {
    return false;
  }

  const isExpired = (c?: IComponent) =>
    getExpiryStatusMessage(c?.end_date) === IExpiredStatus.expired;

  if (isExpired(original) || isExpired(replacement)) {
    return false;
  }

  if (
    original?.end_date &&
    replacement?.end_date &&
    dayjs(replacement?.end_date).isBefore(original?.end_date)
  ) {
    return false;
  }

  return true;
};
